import React, { useState, useEffect } from "react";
import { AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { getConversionRules, convertStock } from "../../api/conversionService";

export default function PreCheckoutHookModal({
  isOpen,
  onClose,
  onProceed,
  outOfStockItems,
  isAutoConversionEnabled
}) {
  const [processing, setProcessing] = useState(false);
  const [conversionLogs, setConversionLogs] = useState([]);

  if (!isOpen) return null;

  const handleProceed = async () => {
    if (!isAutoConversionEnabled) {
      // Just proceed, no conversion
      onProceed();
      return;
    }

    setProcessing(true);
    const logs = [];

    try {
      for (const item of outOfStockItems) {
        const productId = item.item.product_id;
        
        try {
          const rulesRes = await getConversionRules({ product_id: productId });
          const rules = rulesRes?.data || [];
          
          if (rules.length > 0) {
            // Find a valid rule. For simplicity, just use the first rule that converts to this unit or base unit.
            // A more complex logic would check from_unit stock. We'll just attempt the first active rule.
            const rule = rules.find(r => r.is_active);
            if (rule) {
              const qtyToConvert = Math.ceil(item.shortage / rule.conversion_factor);
              
              await convertStock({
                product_id: productId,
                variant_id: item.item.product_variant_id || undefined,
                from_unit_id: rule.from_unit_id,
                to_unit_id: rule.to_unit_id,
                qty: qtyToConvert,
                notes: "Auto-conversion from POS Pre-Checkout"
              });
              
              logs.push(`Konversi berhasil untuk ${item.item.item_name}`);
            } else {
              logs.push(`Tidak ada aturan aktif untuk ${item.item.item_name}`);
            }
          }
        } catch (err) {
          console.error("Auto conversion failed for item", item, err);
          logs.push(`Konversi gagal untuk ${item.item.item_name}`);
        }
      }
      
      setConversionLogs(logs);
      
      // Short delay to let user see logs, then proceed
      setTimeout(() => {
        onProceed();
      }, 1000);
      
    } catch (error) {
      console.error(error);
      onProceed(); // still proceed even if auto conversion fails
    }
  };

  return (
    <>
      <div
        onClick={!processing ? onClose : undefined}
        className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm"></div>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 w-full max-w-lg pointer-events-auto shadow-2xl overflow-hidden p-6">
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Peringatan Stok Kurang
              </h2>
              <p className="text-sm text-slate-500">
                Beberapa item tidak memiliki stok yang mencukupi.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 max-h-48 overflow-y-auto">
            <ul className="space-y-3">
              {outOfStockItems.map((item, idx) => (
                <li key={idx} className="flex justify-between items-center text-sm">
                  <span className="font-medium text-slate-700">{item.item.item_name}</span>
                  <div className="text-right">
                    <div className="text-rose-600 font-semibold">Kurang {item.shortage}</div>
                    <div className="text-slate-500 text-xs">Stok: {item.current_stock}, Butuh: {item.item.quantity}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {isAutoConversionEnabled && (
            <div className="mb-6 p-4 bg-teal-50 border border-teal-100 rounded-lg">
              <p className="text-sm text-teal-800 font-medium">
                Sistem akan mencoba melakukan Konversi Stok Otomatis berdasarkan aturan yang ada.
              </p>
              {conversionLogs.length > 0 && (
                <ul className="mt-2 text-xs text-teal-700 space-y-1">
                  {conversionLogs.map((log, i) => (
                    <li key={i}>• {log}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-5 py-2.5 rounded-xl font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
              Batal
            </button>
            <button
              onClick={handleProceed}
              disabled={processing}
              className="px-5 py-2.5 rounded-xl font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2">
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Memproses...
                </>
              ) : (
                <>
                  Lanjutkan Transaksi
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
